/**
 * AuditHistoryPanel
 *
 * Reusable component that displays change history for any resource
 * with rollback capabilities. Uses the audit trail API endpoints.
 *
 * [T92P2] Auto-Snapshots: History & Rollback Frontend
 */
import { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Plus, Pencil, Trash2, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import api, { endpoints } from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditHistoryPanelProps {
  resource: 'transaction' | 'budget' | 'goal' | 'account';
  resourceId: string;
  onRollback?: () => void;
}

interface AuditEntry {
  _id: string;
  changeType: 'create' | 'update' | 'delete';
  changedFields?: string[];
  timestamp: string;
  previousState: Record<string, unknown>;
}

interface HistoryResponse {
  success: boolean;
  count: number;
  total: number;
  page: number;
  totalPages: number;
  data: AuditEntry[];
}

// ---------------------------------------------------------------------------
// Change-type icon config
// ---------------------------------------------------------------------------

const changeTypeConfig = {
  create: {
    icon: Plus,
    label: 'Created',
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
  },
  update: {
    icon: Pencil,
    label: 'Updated',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
  },
  delete: {
    icon: Trash2,
    label: 'Deleted',
    iconColor: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
  },
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AuditHistoryPanel({
  resource,
  resourceId,
  onRollback,
}: AuditHistoryPanelProps) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<AuditEntry | null>(null);
  const [rollingBack, setRollingBack] = useState(false);

  const fetchHistory = useCallback(
    async (pageNum: number, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        const url = `${endpoints.audit.history(resource, resourceId)}?page=${pageNum}&limit=10`;
        const res = await api.get<HistoryResponse>(url);
        const body = res.data;

        if (append) {
          setEntries((prev) => [...prev, ...body.data]);
        } else {
          setEntries(body.data);
        }
        setTotalPages(body.totalPages);
        setPage(pageNum);
      } catch {
        toast({
          title: 'Failed to load history',
          description: 'Could not fetch change history. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [resource, resourceId, toast],
  );

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const handleLoadMore = () => {
    if (page < totalPages) {
      fetchHistory(page + 1, true);
    }
  };

  const handleRollback = async () => {
    if (!rollbackTarget) return;
    setRollingBack(true);
    try {
      await api.post(
        endpoints.audit.rollback(resource, resourceId, rollbackTarget._id),
      );
      toast({ title: 'Restored successfully' });
      setRollbackTarget(null);
      // Refresh history and notify parent
      await fetchHistory(1);
      onRollback?.();
    } catch {
      toast({
        title: 'Rollback failed',
        description: 'Could not restore this version. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRollingBack(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-3">
          <RotateCcw className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No changes recorded yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-1">
        {entries.map((entry, index) => {
          const config = changeTypeConfig[entry.changeType] ?? changeTypeConfig.update;
          const EntryIcon = config.icon;
          const isFirst = index === 0;

          return (
            <div
              key={entry._id}
              className="flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              {/* Icon */}
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.iconBg}`}
              >
                <EntryIcon className={`h-3.5 w-3.5 ${config.iconColor}`} />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{config.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(entry.timestamp), {
                      addSuffix: true,
                    })}
                  </span>
                </div>

                {/* Changed fields */}
                {entry.changeType === 'update' &&
                  entry.changedFields &&
                  entry.changedFields.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">
                      Changed: {entry.changedFields.join(', ')}
                    </p>
                  )}
              </div>

              {/* Restore button (not on the most recent / current entry) */}
              {!isFirst && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 h-8 text-xs gap-1"
                  onClick={() => setRollbackTarget(entry)}
                >
                  <RotateCcw className="h-3 w-3" />
                  Restore
                </Button>
              )}
            </div>
          );
        })}

        {/* Load more */}
        {page < totalPages && (
          <div className="px-4 pt-2 pb-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={loadingMore}
              onClick={handleLoadMore}
            >
              {loadingMore ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Loading…
                </>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Rollback confirmation dialog */}
      <AlertDialog
        open={!!rollbackTarget}
        onOpenChange={(open) => !open && setRollbackTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to this version?</AlertDialogTitle>
            <AlertDialogDescription>
              Current state will be saved as a snapshot before restoring.
              You can undo this action by restoring the latest snapshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rollingBack}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollback} disabled={rollingBack}>
              {rollingBack ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring…
                </>
              ) : (
                'Restore'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
